import { useLayoutEffect, useRef, useState, type RefObject } from "react";

/**
 * Measure a container and return the side of the LARGEST square that fits inside it — bounded by
 * BOTH its width and its height. A game board sized with this can never overflow the space left
 * after variable-height chrome (profile strips, captured-piece trays, control bars), on any
 * viewport, and without a fragile `min(..vw, ..vh)` guess that breaks once the chrome grows.
 *
 * Why measured (not CSS `aspect-ratio` + `max-*`): a square that must shrink on BOTH axes to fit a
 * flex box is exactly the case where the pure-CSS patterns misbehave across engines (and the
 * Telegram WebView in particular). A ResizeObserver is deterministic everywhere.
 *
 * @param max  hard cap on the side (px) — the board never grows past this on a big screen.
 * @param pad  px to subtract on each measure (room for the board's frame/shadow inside the box).
 * @param reserveHeight
 *   px of the measured box's HEIGHT that is already spoken for by SIBLINGS of the board inside it
 *   (a die slot, a hint line, a control reserve). Subtracted from the height axis ONLY — the width
 *   basis is untouched, so passing 0 (the default) is byte-identical to not passing it at all.
 *
 *   This exists because measuring the container that ALSO holds the chrome silently over-sizes the
 *   square: it is fitted to the full height, the chrome is then laid out below it, the column
 *   overflows, and a `justify-center` + `overflow-hidden` parent CLIPS the board's top edge — which
 *   on screen reads as the board sliding underneath whatever sits above it. (Measured on منچ: 38px
 *   of board gone at 440×700, 66px at 440×520.)
 */
export function useFitSquare<T extends HTMLElement = HTMLDivElement>(
  max = Infinity,
  pad = 0,
  reserveHeight = 0,
): {
  ref: RefObject<T>;
  size: number;
} {
  const ref = useRef<T>(null);
  const [size, setSize] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): void =>
      setSize(
        Math.min(max, Math.max(0, Math.min(el.clientWidth, el.clientHeight - reserveHeight) - pad)),
      );
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [max, pad, reserveHeight]);
  return { ref, size };
}

/**
 * Measure a flex-1 box's inner height and return it, so a NON-square board (e.g. backgammon) can be
 * sized to EXACTLY the available height with an explicit px value instead of relying on its own
 * `flex: 1` + `min-height: 0` to shrink. That reliance is exactly what the Telegram/Android WebView
 * mishandles for a board full of percentage-height children — the board refuses to shrink, so the
 * control bar (روll / confirm) drops below the fold and the game is "unplayable". An explicit
 * measured height sidesteps flex-shrink entirely.
 */
export function useBoxHeight<T extends HTMLElement = HTMLDivElement>(): {
  ref: RefObject<T>;
  height: number;
  /** The box's inner WIDTH, measured on the same observer. A non-square board that holds a target
   *  aspect ratio must solve against both axes — height alone leaves it width-blind, which is how
   *  تخته نرد ended up correctly proportioned on a tall phone and badly flat (a 3.4:1 point instead
   *  of 5.5:1) on the wide-but-short Telegram Desktop window. */
  width: number;
} {
  const ref = useRef<T>(null);
  const [box, setBox] = useState({ height: 0, width: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): void =>
      setBox((prev) => {
        /*
         * ⚠️ THE CONTENT BOX, NOT `clientHeight` — `clientHeight` INCLUDES PADDING.
         *
         * تخته نرد's table group pads its bottom by `BOARD_PAD_BOTTOM + var(--game-safe-bottom)`
         * (the home indicator), and the board budget subtracts only the first of those. Measuring
         * `clientHeight` therefore counted the home-indicator inset as available AND reserved it:
         * the board grew into space that was already spoken for, the column overflowed, and
         * `justify-center` split the overflow — pushing the OPPONENT'S PANEL up over the header.
         *
         * MEASURED at 390×780 with iPhone insets (47 top / 34 bottom): the panel overlapped the
         * exit button by 40×8px. With ZERO insets — which is all a headless harness reports — there
         * was no overlap at all, which is exactly why it was only ever seen on a device.
         */
        const style = getComputedStyle(el);
        const padY = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
        const padX = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
        const height = Math.max(0, el.clientHeight - padY);
        const width = Math.max(0, el.clientWidth - padX);
        return prev.height === height && prev.width === width ? prev : { height, width };
      });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, height: box.height, width: box.width };
}
